// Drawing a post: the theme it wears (design family and colours), its fonts
// and photos, and turning the slides into JPEGs to share.
import { FAMILIES, tunedFamily, themeFonts, layoutFamily, layoutTypeForFont } from './families'
import { COLOURWAYS, coloursFor, tunedColours } from './colourways'
import { fontsHref, stackOf } from './fonts'
import { frameFor } from './kit'
import { drawSlide, imagesFor } from './layouts'
import { loadImages } from './images'
import { slidesFor } from '../data/postTypes'
import { dayOnPost } from '../data/dates'

export const TEXT_SIZES = [0.86, 0.93, 1, 1.08, 1.16]

// The type a post is designed in, before the salon's own font changes: its
// family's, or for the layouts, its theme's (a brand theme follows the
// brand's own headline font)
export const baseFamilyOf = (post, kit) => {
  if (post.family !== 'layouts') return FAMILIES[post.family] || FAMILIES.editorial
  const way = COLOURWAYS.layouts.find(c => c.id === post.colourway)
  return layoutFamily(way ? way.type : layoutTypeForFont(kit && kit.display))
}
// Faces the layouts use beside their theme's: a serif for quotes and notes,
// a heavy face for big numbers, a typewriter face for receipts and tickets
const LAYOUT_EXTRA = [{ key: 'fraunces', weight: 400, italic: false }, { key: 'archivoBlack', weight: 400, italic: false }, { key: 'courierPrime', weight: 400, italic: false }, { key: 'courierPrime', weight: 700, italic: false }]

// What a post draws with: its family's type, its colourway's colours, the
// salon's name (or logo) and the salon's own fine-tuning (post.tune)
export const postTheme = (post, { kit } = {}) => {
  const tune = post.tune || {}
  const base = baseFamilyOf(post, kit)
  const family = tunedFamily(base, tune)
  const c = tunedColours(coloursFor(base.id, post.colourway, { brandColour: kit && kit.accent, photoColour: post.photoColour }), tune.colours, base.id)
  const name = (kit && kit.name) || 'Your salon'
  const logo = tune.brand === 'logo' && kit && kit.logo ? kit.logo : null
  const brandMode = tune.brand === 'none' ? 'none' : logo ? 'logo' : 'name'
  const size = TEXT_SIZES.includes(Number(tune.size)) ? Number(tune.size) : 1
  const hideCta = !!tune.hideCta
  const fontKey = `${family.display}/${family.body.font}`
  const fontChosen = family.display !== base.display
  const extraFonts = base.id === 'layouts' ? LAYOUT_EXTRA : []
  return {
    family, c, name, logo, brandMode, size, hideCta, fontKey, fontChosen, extraFonts,
    key: `${base.id}|${post.layout || ''}|${c.id}|${c.paper}${c.ink}${c.accent}${c.pop}${c.on}|${name}|${fontKey}|${brandMode}${logo || ''}|${size}|${hideCta ? 1 : 0}`,
  }
}

// Web fonts must be loaded before the canvas draws with them
const linked = new Set()
const loadedFonts = new Map()
export const loadFonts = (theme) => {
  const fonts = [...themeFonts(theme.family), ...(theme.extraFonts || [])]
  const key = fonts.map(f => `${f.key}${f.italic ? 'i' : ''}${f.weight}`).join(',')
  if (loadedFonts.has(key)) return loadedFonts.get(key)
  const job = (async () => {
    const href = fontsHref(fonts.map(f => f.key))
    if (href && !linked.has(href) && typeof document !== 'undefined') {
      linked.add(href)
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
      await new Promise((resolve) => { link.onload = resolve; link.onerror = resolve; setTimeout(resolve, 2500) })
    }
    if (typeof document === 'undefined' || !document.fonts || !document.fonts.load) return
    const first = (k) => stackOf(k).split(',')[0]
    await Promise.race([
      Promise.all(fonts.map(f => document.fonts.load(`${f.italic ? 'italic ' : ''}${f.weight} 60px ${first(f.key)}`).catch(() => null))),
      new Promise(r => setTimeout(r, 3000)),
    ])
  })()
  loadedFonts.set(key, job)
  return job
}

// Everything a post needs before it can be drawn
export const preparePost = async (post, theme, { small = false } = {}) => {
  const srcs = [...imagesFor(post, theme), theme && theme.logo].filter(Boolean)
  const [images] = await Promise.all([loadImages(srcs, { small }), loadFonts(theme)])
  return images
}

// Draw one slide onto a canvas at a scale (1 = full 1080px). Returns the tap
// targets in 1080px units.
export const renderSlide = (canvas, { post, theme, images, format = post.format || 'post', slideIndex = 0, scale = 1 }) => {
  const frame = frameFor(format)
  const slides = slidesFor(post, format)
  const slide = slides[Math.min(slideIndex, slides.length - 1)] || slides[0]
  const w = Math.round(frame.W * scale)
  const h = Math.round(frame.H * scale)
  if (canvas.width !== w) canvas.width = w
  if (canvas.height !== h) canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.setTransform(scale, 0, 0, scale, 0, 0)
  return drawSlide(ctx, { post, theme, images: images || {}, slide, format, frame, W: frame.W, H: frame.H, day: dayOnPost(post) })
}

const toBlob = (canvas, quality = 0.92) => new Promise((resolve, reject) => {
  try {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('export'))), 'image/jpeg', quality)
  } catch (e) {
    // A photo without CORS headers "taints" the canvas and it can't be saved
    reject(e)
  }
})

// Every slide as a full-size JPEG. Throws if a photo can't be exported.
export const exportPost = async (post, theme, format = post.format || 'post') => {
  const images = await preparePost(post, theme)
  const slides = slidesFor(post, format)
  const out = []
  for (let i = 0; i < slides.length; i += 1) {
    const c = document.createElement('canvas')
    renderSlide(c, { post, theme, images, format, slideIndex: i, scale: 1 })
    // eslint-disable-next-line no-await-in-loop
    out.push(await toBlob(c))
  }
  return out
}

// A small picture of the first slide for lists and the calendar
export const thumbnail = async (post, theme, width = 240) => {
  const images = await preparePost(post, theme, { small: true })
  const c = document.createElement('canvas')
  renderSlide(c, { post, theme, images, format: post.format || 'post', slideIndex: 0, scale: width / 1080 })
  try { return c.toDataURL('image/jpeg', 0.74) } catch (e) { return null }
}
